import React, { useState } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
  List,
  ListItem,
  ListItemText,
  Checkbox,
  Select,
  MenuItem,
  InputLabel,
} from '@mui/material'
import { toast } from 'react-toastify'
import { roadmapAPI } from '../api/roadmapAPI'
import { useTranslation } from 'react-i18next' // LAP-733 (GE022)

const RolloverModal = ({ open, onClose, session, onComplete }) => {
  const { t } = useTranslation('layout') // LAP-733 (GE022)
  const [decision, setDecision] = useState('move_all')
  const [selectedItems, setSelectedItems] = useState({})

  if (!session || !session.items) return null

  const handleDecisionChange = (event) => {
    setDecision(event.target.value)
  }

  const handleItemToggle = (itemId) => {
    setSelectedItems({
      ...selectedItems,
      [itemId]: !selectedItems[itemId],
    })
  }

  const handleApply = async () => {
    try {
      if (decision === 'move_all') {
        // Apply MOVE decision to all items
        for (const item of session.items) {
          await roadmapAPI.applyRolloverDecision(
            session.id,
            item.id,
            'MOVE'
          )
        }
        toast.success(t('roadmap.toastAllMovedToNext'))
      } else if (decision === 'review') {
        // Apply individual decisions
        for (const item of session.items) {
          const itemDecision = selectedItems[item.id]
          if (itemDecision === 'MOVE') {
            await roadmapAPI.applyRolloverDecision(
              session.id,
              item.id,
              'MOVE'
            )
          } else if (itemDecision === 'ABANDON') {
            await roadmapAPI.applyRolloverDecision(
              session.id,
              item.id,
              'ABANDON'
            )
          } else if (itemDecision === 'MOVE_TO_DREAMS') {
            await roadmapAPI.applyRolloverDecision(
              session.id,
              item.id,
              'MOVE_TO_DREAMS'
            )
          }
        }
        toast.success(t('roadmap.toastDecisionsApplied'))
      } else if (decision === 'abandon_some') {
        // Mark selected items as abandoned
        for (const itemId of Object.keys(selectedItems)) {
          if (selectedItems[itemId]) {
            await roadmapAPI.applyRolloverDecision(
              session.id,
              itemId,
              'ABANDON'
            )
          } else {
            await roadmapAPI.applyRolloverDecision(
              session.id,
              itemId,
              'MOVE'
            )
          }
        }
        toast.success(t('roadmap.toastDecisionsApplied'))
      } else if (decision === 'move_to_dreams') {
        // Move all to dreams
        for (const item of session.items) {
          await roadmapAPI.applyRolloverDecision(
            session.id,
            item.id,
            'MOVE_TO_DREAMS'
          )
        }
        toast.success(t('roadmap.toastAllMovedToDreams'))
      }

      onComplete()
    } catch (error) {
      toast.error(t('roadmap.toastErrDecisionsApply'))
      console.error(error)
    }
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          background: 'rgba(0, 0, 29, 0.95)',
          backdropFilter: 'blur(10px)',
          borderRadius: '16px',
        },
      }}
    >
      <DialogTitle
        sx={{
          color: 'white',
          fontSize: '1.5rem',
          fontWeight: 500,
        }}
      >
        {t('roadmap.rolloverTitle')}
      </DialogTitle>
      <DialogContent>
        <Typography
          variant="body1"
          sx={{
            color: 'rgba(255, 255, 255, 0.8)',
            mb: 3,
          }}
        >
          {t('roadmap.rolloverIntroFull')}
        </Typography>

        <FormControl component="fieldset" sx={{ width: '100%' }}>
          <RadioGroup
            value={decision}
            onChange={handleDecisionChange}
            sx={{ color: 'white' }}
          >
            <FormControlLabel
              value="move_all"
              control={<Radio sx={{ color: 'white' }} />}
              label={t('roadmap.rolloverOptionMoveAll')}
            />
            <FormControlLabel
              value="review"
              control={<Radio sx={{ color: 'white' }} />}
              label={t('roadmap.rolloverOptionReview')}
            />
            <FormControlLabel
              value="abandon_some"
              control={<Radio sx={{ color: 'white' }} />}
              label={t('roadmap.rolloverOptionAbandon')}
            />
            <FormControlLabel
              value="move_to_dreams"
              control={<Radio sx={{ color: 'white' }} />}
              label={t('roadmap.rolloverOptionDreams')}
            />
          </RadioGroup>
        </FormControl>

        {decision === 'review' && (
          <Box sx={{ mt: 3 }}>
            <Typography
              variant="h6"
              sx={{ color: 'white', mb: 2 }}
            >
              {t('roadmap.rolloverReviewList')}
            </Typography>
            <List
              sx={{
                backgroundColor: 'rgba(255, 255, 255, 0.05)',
                borderRadius: '8px',
                p: 1,
              }}
            >
              {session.items.map((item) => (
                <ListItem key={item.id}>
                  <ListItemText
                    primary={item.idea?.title || t('roadmap.idea')}
                    sx={{ color: 'white' }}
                  />
                  <FormControl size="small" sx={{ minWidth: 150 }}>
                    <Select
                      value={selectedItems[item.id] || 'MOVE'}
                      onChange={(e) =>
                        setSelectedItems({
                          ...selectedItems,
                          [item.id]: e.target.value,
                        })
                      }
                      sx={{
                        color: 'white',
                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: 'rgba(255, 255, 255, 0.3)',
                        },
                        '& .MuiSvgIcon-root': {
                          color: 'rgba(255, 255, 255, 0.7)',
                        },
                      }}
                    >
                      <MenuItem value="MOVE">{t('roadmap.rolloverDecisionMove')}</MenuItem>
                      <MenuItem value="ABANDON">{t('roadmap.rolloverDecisionAbandon')}</MenuItem>
                      <MenuItem value="MOVE_TO_DREAMS">{t('roadmap.rolloverDecisionDreams')}</MenuItem>
                    </Select>
                  </FormControl>
                </ListItem>
              ))}
            </List>
          </Box>
        )}

        {decision === 'abandon_some' && (
          <Box sx={{ mt: 3 }}>
            <Typography
              variant="h6"
              sx={{ color: 'white', mb: 2 }}
            >
              {t('roadmap.rolloverAbandonList')}
            </Typography>
            <List
              sx={{
                backgroundColor: 'rgba(255, 255, 255, 0.05)',
                borderRadius: '8px',
                p: 1,
              }}
            >
              {session.items.map((item) => (
                <ListItem key={item.id}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={selectedItems[item.id] || false}
                        onChange={() => handleItemToggle(item.id)}
                        sx={{ color: 'white' }}
                      />
                    }
                    label={
                      <Typography sx={{ color: 'white' }}>
                        {item.idea?.title || t('roadmap.idea')}
                      </Typography>
                    }
                  />
                </ListItem>
              ))}
            </List>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
          {t('roadmap.rolloverLater')}
        </Button>
        <Button onClick={handleApply} variant="contained">
          {t('roadmap.rolloverApply')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default RolloverModal

